/**
 * @module useMeals
 * Shared state and operations for the Fusion Eat admin module (client
 * companies, employee rosters + personal ordering links, the meal-set
 * catalog, weekly menu notes, the orders board, the kitchen production
 * sheet, and the monthly billing report).
 *
 * Singleton composable: all refs are module-scoped so every component
 * shares the same state. Pattern follows useHrEmployees.js — thin views,
 * all logic here.
 */
import { ref, computed } from "vue";
import { periodRange } from "@/utils/datePresets.js";
import { mealsApi, assetsApi } from "@/utils/api.js";

// ── Shared state ─────────────────────────────────────────────────────────────

const snackbar = ref({ show: false, message: "", color: "success" });
const showSnackbar = (message, color = "success") => {
  snackbar.value = { show: true, message, color };
};
const showError = (err, fallback) => {
  showSnackbar(err?.response?.data?.error || fallback, "error");
};

// ── Client Companies ─────────────────────────────────────────────────────────

const clients = ref([]);
const clientsLoading = ref(false);
const clientDialog = ref(false);
const editingClient = ref(null); // null = create
const clientForm = ref(emptyClientForm());
const clientSaving = ref(false);

function emptyClientForm() {
  return {
    name: "",
    // Short abbreviation (e.g. "EQU") used to auto-build each of this
    // client's employee dispatcher codes ("EQU-001"). Optional — leave blank
    // and new employees just won't get one auto-generated.
    code: "",
    address: "",
    delivery_area: "",
    rate_per_meal_aed: 45.0,
    delivery_fee_aed: 10.0,
    min_order_meals: 5,
    billing_contact_name: "",
    billing_contact_email: "",
    billing_contact_phone: "",
    // What the invoice must say. The legal entity that pays is often not the
    // office that eats — Equiti's Al Ain staff are billed through a Dubai
    // entity with its own address and TRN — so these are separate from
    // `address`, which is where the food goes. A UAE tax invoice has to name
    // the buyer's TRN, so it has its own field rather than a line of prose.
    billing_entity_name: "",
    bank_payer_name: "",
    billing_address: "",
    billing_po_box: "",
    billing_trn: "",
    invoice_cycle: "monthly",
    // Monday's automatic weekly invoice (issued + e-mailed), weekly clients only
    auto_invoice: false,
    // The day-to-day catering coordinator at this client — often a
    // different person than the billing contact. office_manager_phone
    // gates the "send this employee's link via WhatsApp" action; email
    // gates the "send via email" one — either, both, or neither can be set.
    office_manager_name: "",
    office_manager_email: "",
    office_manager_phone: "",
    // 'weekly' (default since 2026-09-17): one cutoff — the preceding Friday —
    // for the whole following week, which is what the client agreements say and
    // what buying in advance needs. 'daily': rolling cutoff the evening before.
    order_mode: "weekly",
    // Deadline hour, Dubai time (0-23). Per client since 2026-09-17 — Equiti
    // decides late in the afternoon, another client may want it earlier.
    cutoff_hour: 21,
    // Order emails. Blank subject/body = the built-in default text (the server
    // falls back), so an admin only fills these in to override the wording.
    notify_office_manager: true,
    summary_subject: "",
    summary_body: "",
    reminder_subject: "",
    reminder_body: "",
    // Every set already includes "a soft drink and water" either way — this
    // only controls whether employees get to say *which* soft drink.
    allow_drink_selection: true,
    // Fallback drink when an order has no explicit choice — either because
    // the employee hasn't picked one, or allow_drink_selection is off.
    default_drink_id: null,
    // What the agreed rate per meal includes besides the main and the drink (migration 035)
    includes_salad: true,
    includes_dessert: true,
    // Meal photos in the ordering app (migration 036)
    show_images: true,
  };
}

// ── Console filters (2026-09-17) ─────────────────────────────────────────────
// One set of filters for the whole module: pick client / employee / dates once,
// then every report answers with the same scope. Maurizio: "a search bar with
// client|employee|date (shortcut: this week last week, next week) ... once a
// report is clicked it should be auto-loaded based on the search filters".
const filters = ref({ clientId: null, employeeId: null, start: "", end: "" });
const datePreset = ref("thisWorkWeek");

/** Date shortcuts. Fusion Eat delivers Mon-Fri, so a "week" is Monday to Friday. */
// Days first, then the weeks in calendar order — last, this, next. "Last
// week" used to sit between "This week" and "Next week", which read as a
// jumble (Maurizio, 2026-09-18: "move last week first").
// Which report the console is showing. Shared state rather than local to
// MealsHome, because the app-bar dots menu switches it from outside the page
// (Maurizio, 2026-09-18: the seldom-used reports moved off the button row).
const report = ref("orders");

// The shortcuts are the shared periods (utils/datePresets.js — Dubai days,
// working weeks Monday–Friday); only the short chip titles are this page's.
const datePresets = [
  { value: "today", title: "Today" },
  { value: "tomorrow", title: "Tomorrow" },
  { value: "lastWorkWeek", title: "Last week" },
  { value: "thisWorkWeek", title: "This week" },
  { value: "nextWorkWeek", title: "Next week" },
];

function applyDatePreset(name) {
  const [start, end] = periodRange(name);
  filters.value.start = start;
  filters.value.end = end;
  datePreset.value = name;
}
applyDatePreset("thisWorkWeek");

async function fetchClients({ includeInactive = false } = {}) {
  clientsLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/clients", { params: { includeInactive: includeInactive ? "1" : undefined } });
    clients.value = data;
  } catch (err) {
    showError(err, "Failed to load client companies");
  } finally {
    clientsLoading.value = false;
  }
}

// Proposes a client code from the company name — first alphabetic word,
// first 3 letters, uppercased (mirrors the existing hand-set convention,
// e.g. "Equiti Group" -> "EQU"). Just a starting point: still a plain
// editable text field, so it can be tweaked or cleared before saving.
function suggestClientCode(name) {
  const firstWord = (name || "").trim().split(/\s+/)[0] || "";
  return firstWord.replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase();
}

function openClientDialog(client = null) {
  editingClient.value = client;
  clientForm.value = client
    ? { ...client, code: client.code || suggestClientCode(client.name) }
    : emptyClientForm();
  clientDialog.value = true;
}

function closeClientDialog() {
  clientDialog.value = false;
  editingClient.value = null;
}

async function saveClient() {
  clientSaving.value = true;
  try {
    if (editingClient.value) {
      await mealsApi.put(`/api/meals/admin/clients/${editingClient.value.id}`, clientForm.value);
      showSnackbar("Client company updated");
    } else {
      await mealsApi.post("/api/meals/admin/clients", clientForm.value);
      showSnackbar("Client company created");
    }
    closeClientDialog();
    await fetchClients();
  } catch (err) {
    showError(err, "Failed to save client company");
  } finally {
    clientSaving.value = false;
  }
}

// The Fusion Eat client the admin is currently working with — shared across
// every tab (Clients & Employees, Orders, Production's weekly schedule,
// Procurement, Billing) so picking one in any tab carries over to the rest
// instead of having to re-pick it per tab, and the app bar can show it as a
// persistent "which client am I looking at" indicator (see MealsHome.vue).
// null means "no client chosen" — read by each tab according to its own
// semantics (Orders/Production treat it as "all clients together"; Billing,
// which can't run without one, falls back to the first client on load).
const selectedClientId = ref(null);
const selectedClient = computed(() => clients.value.find((c) => c.id === selectedClientId.value) || null);

async function setClientActive(client, active) {
  try {
    await mealsApi.put(`/api/meals/admin/clients/${client.id}/active`, { active });
    showSnackbar(active ? "Client company reactivated" : "Client company deactivated");
    await fetchClients({ includeInactive: true });
  } catch (err) {
    showError(err, "Failed to update client company");
  }
}

// ── Employees ────────────────────────────────────────────────────────────────

const employees = ref([]);
const employeesLoading = ref(false);
const employeeDialog = ref(false);
const editingEmployee = ref(null);
const employeeForm = ref(emptyEmployeeForm());
const employeeSaving = ref(false);

function emptyEmployeeForm() {
  // employee_code: blank here means "auto-generate from the client's code
  // when saved" (see database.js createEmployee) — left blank rather than
  // pre-filled so the dialog doesn't imply a value that isn't decided yet.
  // summary_emails: on by default, and only meaningful once an email is set.
  // address/pin/plan_*: individual customers only (the built-in "Individual
  // customers" client) — the server ignores them for company employees.
  return {
    full_name: "", email: "", phone: "", dietary_notes: "", employee_code: "", summary_emails: true,
    address: "", pin: "", plan_meals_per_week: 3, plan_period: "week", plan_start: "",
    plan_salad: true, plan_dessert: true,
    // v2 (migration 038): lunch only or lunch and dinner, which is lighter, and the drink as an option
    plan_meals_per_day: 1, plan_light_meal: "dinner", plan_drink: false,
  };
}

async function fetchEmployees(clientId, { includeInactive = false } = {}) {
  if (!clientId) return;
  employeesLoading.value = true;
  try {
    const { data } = await mealsApi.get(`/api/meals/admin/clients/${clientId}/employees`, {
      params: { includeInactive: includeInactive ? "1" : undefined },
    });
    employees.value = data;
  } catch (err) {
    showError(err, "Failed to load employees");
  } finally {
    employeesLoading.value = false;
  }
}

// Previews the code a new employee would actually be assigned on save
// (createEmployee falls back to this same "<client code>-<seq>" scheme
// server-side when employee_code is left blank) — mirrors nextEmployeeCode()
// using the roster already loaded for this client. Just a starting point:
// still a plain editable/clearable field.
function suggestEmployeeCode(client) {
  if (!client?.code) return "";
  // One past the highest, like the server: counting heads re-offered a code
  // still in use once someone left (EQU-009 offered while Issa Jarad had it, 16 Oct 2026)
  const highest = Math.max(0, ...employees.value.map((e) => Number(/-(\d+)$/.exec(e.employee_code || "")?.[1]) || 0));
  return `${client.code}-${String(highest + 1).padStart(3, "0")}`;
}

function openEmployeeDialog(employee = null, client = null) {
  editingEmployee.value = employee;
  employeeForm.value = employee
    ? {
        full_name: employee.full_name,
        email: employee.email || "",
        phone: employee.phone || "",
        dietary_notes: employee.dietary_notes || "",
        // The code was missing from this branch, so every edit of an existing
        // employee submitted a blank one and the update wrote NULL over their
        // dispatcher code (Maurizio, 2026-09-18: "why no code for these 2
        // employees" — they were the two I had just re-saved).
        employee_code: employee.employee_code || "",
        summary_emails: employee.summary_emails !== false,
        address: employee.address || "",
        pin: employee.lat != null && employee.lng != null ? `${Number(employee.lat)}, ${Number(employee.lng)}` : "",
        plan_meals_per_week: employee.plan_meals_per_week || 3,
        plan_period: employee.plan_period || "week",
        plan_start: employee.plan_start ? String(employee.plan_start).slice(0, 10) : "",
        plan_salad: employee.plan_salad !== false,
        plan_dessert: employee.plan_dessert !== false,
        plan_meals_per_day: employee.plan_meals_per_day === 2 ? 2 : 1,
        plan_light_meal: employee.plan_light_meal || "dinner",
        plan_drink: employee.plan_drink === true,
      }
    : { ...emptyEmployeeForm(), employee_code: suggestEmployeeCode(client) };
  employeeDialog.value = true;
}

function closeEmployeeDialog() {
  employeeDialog.value = false;
  editingEmployee.value = null;
}

async function saveEmployee(clientId) {
  employeeSaving.value = true;
  try {
    const { data } = editingEmployee.value
      ? await mealsApi.put(`/api/meals/admin/employees/${editingEmployee.value.id}`, employeeForm.value)
      : await mealsApi.post(`/api/meals/admin/clients/${clientId}/employees`, employeeForm.value);
    // An individual beyond the delivery radius is saved, with a warning to act on
    if (data?.warning) showSnackbar(`Saved — ${data.warning}`, "warning");
    else showSnackbar(editingEmployee.value ? "Saved" : "Added");
    closeEmployeeDialog();
    await fetchEmployees(clientId);
  } catch (err) {
    showError(err, "Failed to save employee");
  } finally {
    employeeSaving.value = false;
  }
}

async function setEmployeeActive(employee, active, clientId) {
  try {
    await mealsApi.put(`/api/meals/admin/employees/${employee.id}/active`, { active });
    showSnackbar(active ? "Employee reactivated" : "Employee deactivated");
    await fetchEmployees(clientId, { includeInactive: true });
  } catch (err) {
    showError(err, "Failed to update employee");
  }
}

async function regenerateLink(employee, clientId) {
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/employees/${employee.id}/regenerate-link`);
    showSnackbar("New personal link issued — the old one no longer works");
    await fetchEmployees(clientId, { includeInactive: true });
    return data.orderLink;
  } catch (err) {
    showError(err, "Failed to regenerate link");
    return null;
  }
}

// ── Late window (2026-11-02) ─────────────────────────────────────────────────
// Opens the employee's own link until today's cutoff for the following days.
async function openLateWindow(employee, clientId) {
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/employees/${employee.id}/late-window`);
    await fetchEmployees(clientId, { includeInactive: true });
    return data; // { orderLink, late_until, lateUntilLabel, … }
  } catch (err) {
    showError(err, "Failed to open the late window");
    return null;
  }
}
async function closeLateWindow(employee, clientId) {
  try {
    await mealsApi.delete(`/api/meals/admin/employees/${employee.id}/late-window`);
    showSnackbar("Late window closed");
    await fetchEmployees(clientId, { includeInactive: true });
  } catch (err) {
    showError(err, "Failed to close the late window");
  }
}

// ── Employee Leave ───────────────────────────────────────────────────────────
// Purely a scheduling aid — billing/production already only count real
// orders. Lets admin tell "on leave" apart from "hasn't ordered yet".

const employeeLeave = ref([]);
const employeeLeaveLoading = ref(false);
const leaveDialog = ref(false);
const leaveEmployee = ref(null); // the employee the dialog is currently for
const leaveForm = ref({ start_date: "", end_date: "", notes: "" });
const leaveSaving = ref(false);

async function fetchEmployeeLeave(employeeId) {
  employeeLeaveLoading.value = true;
  try {
    const { data } = await mealsApi.get(`/api/meals/admin/employees/${employeeId}/leave`);
    employeeLeave.value = data;
  } catch (err) {
    showError(err, "Failed to load leave periods");
  } finally {
    employeeLeaveLoading.value = false;
  }
}

function openLeaveDialog(employee) {
  leaveEmployee.value = employee;
  leaveForm.value = { start_date: "", end_date: "", notes: "" };
  fetchEmployeeLeave(employee.id);
  leaveDialog.value = true;
}

function closeLeaveDialog() {
  leaveDialog.value = false;
  leaveEmployee.value = null;
}

async function addLeave() {
  if (!leaveEmployee.value) return;
  leaveSaving.value = true;
  try {
    await mealsApi.post(`/api/meals/admin/employees/${leaveEmployee.value.id}/leave`, leaveForm.value);
    showSnackbar("Leave recorded — any picks in that range were cleared");
    leaveForm.value = { start_date: "", end_date: "", notes: "" };
    await fetchEmployeeLeave(leaveEmployee.value.id);
  } catch (err) {
    showError(err, "Failed to record leave");
  } finally {
    leaveSaving.value = false;
  }
}

async function removeLeave(leaveId) {
  if (!leaveEmployee.value) return;
  try {
    await mealsApi.delete(`/api/meals/admin/employees/${leaveEmployee.value.id}/leave/${leaveId}`);
    showSnackbar("Leave period removed");
    await fetchEmployeeLeave(leaveEmployee.value.id);
  } catch (err) {
    showError(err, "Failed to remove leave period");
  }
}

// Orders-board view: who's on leave in a date range (across clients or one).
const leaveInRange = ref([]);
async function fetchLeaveInRange(start, end, clientId) {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/leave", { params: { start, end, clientId: clientId || undefined } });
    leaveInRange.value = data;
  } catch (err) {
    showError(err, "Failed to load leave for this period");
  }
}

// ── Meal Sets ────────────────────────────────────────────────────────────────

const mealSets = ref([]);
const mealSetsLoading = ref(false);
const mealSetDialog = ref(false);
const editingMealSet = ref(null);
const mealSetForm = ref(emptyMealSetForm());
const mealSetSaving = ref(false);

function emptyMealSetForm() {
  // client_company_id: null = shared with every client (the default for
  // nearly every set); set it to restrict a client-specific request (e.g. a
  // lamb/beef main) to just that one client's menu.
  return { number: null, category: "", name: "", description: "", description_ar: "", recipe_id: null, client_company_id: null, is_light: false };
}

// Menu-service recipes (cross-DB), for linking a meal set to its ingredients.
const recipes = ref([]);
async function fetchRecipes() {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/recipes");
    recipes.value = data;
  } catch (err) {
    showError(err, "Failed to load recipes");
  }
}

async function fetchMealSets({ includeInactive = false } = {}) {
  mealSetsLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/meal-sets", { params: { includeInactive: includeInactive ? "1" : undefined } });
    mealSets.value = data;
  } catch (err) {
    showError(err, "Failed to load meal sets");
  } finally {
    mealSetsLoading.value = false;
  }
}

// The main dish's photo (migration 034). Chosen in the dialog, sent on Save:
// `file` = a new one (already shrunk), `remove` = take the current one off.
const mealSetPhoto = ref({ file: null, preview: null, remove: false });

/** The public URL the ordering app loads; ?v= changes with each new photo. */
function mealSetImageUrl(set) {
  if (!set?.image_updated_at) return null;
  return `${mealsApi.defaults.baseURL}/api/meals/public/meal-sets/${set.id}/image?v=${encodeURIComponent(set.image_updated_at)}`;
}

/** A phone photo is several MB: send at most 1200 px wide as a JPEG. */
async function shrinkPhoto(file, maxSide = 1200) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("The photo could not be read"))), "image/jpeg", 0.85),
  );
}

async function chooseMealSetPhoto(file) {
  if (!file) return;
  try {
    const blob = await shrinkPhoto(file);
    if (mealSetPhoto.value.file) URL.revokeObjectURL(mealSetPhoto.value.preview);
    mealSetPhoto.value = { file: blob, preview: URL.createObjectURL(blob), remove: false };
  } catch (err) {
    showError(err, "That file is not a photo this browser can read");
  }
}

function removeMealSetPhoto() {
  if (mealSetPhoto.value.file) URL.revokeObjectURL(mealSetPhoto.value.preview);
  mealSetPhoto.value = { file: null, preview: null, remove: !!editingMealSet.value?.image_updated_at };
}

function openMealSetDialog(set = null) {
  editingMealSet.value = set;
  mealSetPhoto.value = { file: null, preview: mealSetImageUrl(set), remove: false };
  // A new set proposes the next free number (inactive sets keep theirs: numbers are unique)
  const nextNumber = Math.max(0, ...mealSets.value.map((s) => Number(s.number) || 0)) + 1;
  mealSetForm.value = set ? { ...set } : { ...emptyMealSetForm(), number: nextNumber };
  mealSetDialog.value = true;
}

function closeMealSetDialog() {
  mealSetDialog.value = false;
  editingMealSet.value = null;
}

async function saveMealSet() {
  mealSetSaving.value = true;
  try {
    let id = editingMealSet.value?.id;
    if (id) {
      await mealsApi.put(`/api/meals/admin/meal-sets/${id}`, mealSetForm.value);
    } else {
      ({ data: { id } } = await mealsApi.post("/api/meals/admin/meal-sets", mealSetForm.value));
    }
    const photo = mealSetPhoto.value;
    if (photo.file) {
      const form = new FormData();
      form.append("image", photo.file, "photo.jpg");
      await mealsApi.put(`/api/meals/admin/meal-sets/${id}/image`, form);
    } else if (photo.remove) {
      await mealsApi.delete(`/api/meals/admin/meal-sets/${id}/image`);
    }
    showSnackbar(editingMealSet.value ? "Meal set updated" : "Meal set created");
    closeMealSetDialog();
    await fetchMealSets({ includeInactive: true });
  } catch (err) {
    showError(err, "Failed to save meal set");
  } finally {
    mealSetSaving.value = false;
  }
}

async function setMealSetActive(set, active) {
  try {
    await mealsApi.put(`/api/meals/admin/meal-sets/${set.id}/active`, { active });
    showSnackbar(active ? "Meal set reactivated" : "Meal set deactivated");
    await fetchMealSets({ includeInactive: true });
  } catch (err) {
    showError(err, "Failed to update meal set");
  }
}

// ── Drinks ───────────────────────────────────────────────────────────────────
// Every set already includes "a soft drink and water" either way — this
// catalog is just which soft drink; whether a client's employees get to
// choose at all is client_companies.allow_drink_selection (see clientForm above).

const drinks = ref([]);
const drinksLoading = ref(false);
const drinkDialog = ref(false);
const editingDrink = ref(null);
const drinkForm = ref({ name: "", sort_order: 0 });
const drinkSaving = ref(false);

async function fetchDrinks({ includeInactive = false } = {}) {
  drinksLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/drinks", { params: { includeInactive: includeInactive ? "1" : undefined } });
    drinks.value = data;
  } catch (err) {
    showError(err, "Failed to load drinks");
  } finally {
    drinksLoading.value = false;
  }
}

function openDrinkDialog(drink = null) {
  editingDrink.value = drink;
  drinkForm.value = drink ? { name: drink.name, sort_order: drink.sort_order } : { name: "", sort_order: (drinks.value.length || 0) + 1 };
  drinkDialog.value = true;
}

function closeDrinkDialog() {
  drinkDialog.value = false;
  editingDrink.value = null;
}

async function saveDrink() {
  drinkSaving.value = true;
  try {
    if (editingDrink.value) {
      await mealsApi.put(`/api/meals/admin/drinks/${editingDrink.value.id}`, drinkForm.value);
      showSnackbar("Drink updated");
    } else {
      await mealsApi.post("/api/meals/admin/drinks", drinkForm.value);
      showSnackbar("Drink added");
    }
    closeDrinkDialog();
    await fetchDrinks({ includeInactive: true });
  } catch (err) {
    showError(err, "Failed to save drink");
  } finally {
    drinkSaving.value = false;
  }
}

async function setDrinkActive(drink, active) {
  try {
    await mealsApi.put(`/api/meals/admin/drinks/${drink.id}/active`, { active });
    showSnackbar(active ? "Drink reactivated" : "Drink deactivated");
    await fetchDrinks({ includeInactive: true });
  } catch (err) {
    showError(err, "Failed to update drink");
  }
}

// ── Weekly Menu Notes ──────────────────────────────────────────────────────────

const weeklyMenuDays = ref([]);
const weeklyMenuLoading = ref(false);

async function fetchWeeklyMenu(start, end) {
  weeklyMenuLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/weekly-menu", { params: { start, end } });
    weeklyMenuDays.value = data;
  } catch (err) {
    showError(err, "Failed to load weekly menu notes");
  } finally {
    weeklyMenuLoading.value = false;
  }
}

async function saveMenuDay(date, ricePotein, unavailableSetIds, label) {
  try {
    await mealsApi.put(`/api/meals/admin/weekly-menu/${date}`, {
      rice_protein: ricePotein || null,
      unavailable_set_ids: unavailableSetIds || [],
    });
    showSnackbar(`${label || date} saved`);
  } catch (err) {
    showError(err, "Failed to update day");
  }
}

// ── Orders Board ─────────────────────────────────────────────────────────────

const orders = ref([]);
const ordersLoading = ref(false);

async function fetchOrders(start, end, clientId) {
  ordersLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/orders", { params: { start, end, clientId: clientId || undefined } });
    orders.value = data;
  } catch (err) {
    showError(err, "Failed to load orders");
  } finally {
    ordersLoading.value = false;
  }
}

// Same endpoint as the orders board, but its own state — kept separate from
// `orders` above so loading the Production tab's pack list never clobbers
// whatever the Orders tab has loaded (both tabs can be mounted at once).
const packList = ref([]);
const packListLoading = ref(false);
async function fetchPackList(date, clientId) {
  packListLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/orders", {
      params: { start: date, end: date, clientId: clientId || undefined },
    });
    packList.value = data;
  } catch (err) {
    showError(err, "Failed to load pack list");
  } finally {
    packListLoading.value = false;
  }
}

// Same idea as packList above, but spanning a date range (the coming week,
// typically) and optionally scoped to one client — the daily pack list
// alone wasn't enough for a kitchen printing a whole week's packing sheet
// at once, and it had no way to isolate a single client's orders at all.
// Which part of the Menu report is open (sets | drinks | notes). Shared so that a
// warning elsewhere can take you straight to Week Notes.
const menuPart = ref("sets");

// How many Rice sets are ordered per day, across ALL clients — the protein is set
// per day, not per client — so Week Notes can say "2 ordered" beside an empty day.
const riceDemand = ref({});
async function fetchRiceDemand(start, end) {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/orders", { params: { start, end } });
    const byDay = {};
    for (const o of data) {
      if (!o.is_rice) continue;
      const day = String(o.order_date).slice(0, 10);
      byDay[day] = (byDay[day] || 0) + 1;
    }
    riceDemand.value = byDay;
  } catch {
    riceDemand.value = {}; // a hint only — never block Week Notes on it
  }
}

const weeklyPackList = ref([]);
const weeklyPackListLoading = ref(false);
async function fetchWeeklyPackList(start, end, clientId) {
  weeklyPackListLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/orders", { params: { start, end, clientId: clientId || undefined } });
    weeklyPackList.value = data;
  } catch (err) {
    showError(err, "Failed to load weekly pack list");
  } finally {
    weeklyPackListLoading.value = false;
  }
}

// meal: "lunch" (default) or "dinner" — an individual's lunch-and-dinner plan (v2)
async function saveOrder(employeeId, orderDate, mealSetId, drinkId = null, meal = "lunch") {
  try {
    await mealsApi.put("/api/meals/admin/orders", { employeeId, orderDate, mealSetId, drinkId, meal });
    showSnackbar("Order saved");
  } catch (err) {
    showError(err, "Failed to save order");
  }
}

async function deleteOrder(employeeId, orderDate, meal = "lunch") {
  try {
    await mealsApi.delete("/api/meals/admin/orders", { params: { employeeId, orderDate, meal } });
    showSnackbar("Order cleared");
  } catch (err) {
    showError(err, "Failed to clear order");
  }
}

// ── Production Sheet ──────────────────────────────────────────────────────────

const production = ref(null);
const productionLoading = ref(false);

async function fetchProduction(date) {
  productionLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/production", { params: { date } });
    production.value = data;
  } catch (err) {
    showError(err, "Failed to load production sheet");
  } finally {
    productionLoading.value = false;
  }
}

// Per-day breakdown across a range (unlike `production` above, which is one
// day's totals) — feeds the printable weekly kitchen schedule for the chef.
const weeklySchedule = ref(null);
const weeklyScheduleLoading = ref(false);

async function fetchWeeklySchedule(start, end, clientId) {
  weeklyScheduleLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/production/week", { params: { start, end, clientId: clientId || undefined } });
    weeklySchedule.value = data;
  } catch (err) {
    showError(err, "Failed to load weekly kitchen schedule");
  } finally {
    weeklyScheduleLoading.value = false;
  }
}

// ── Procurement (ingredient shopping list, cross-DB via menu recipes) ───────

const procurement = ref(null);
const procurementLoading = ref(false);

async function fetchProcurement(start, end, clientId) {
  procurementLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/procurement", { params: { start, end, clientId: clientId || undefined } });
    procurement.value = data;
  } catch (err) {
    showError(err, "Failed to load procurement list");
  } finally {
    procurementLoading.value = false;
  }
}

// ── Billing ──────────────────────────────────────────────────────────────────

const billing = ref(null);
const billingLoading = ref(false);

/**
 * One client's bill for a period. Weekly and monthly are both real cycles, so
 * the caller passes the dates rather than a month (see client_companies
 * .invoice_cycle for what a given client expects).
 */

// ── Who the invoice is from ────────────────────────────────────────────────
// Our own identity and bank details come from the server, not the bundle: as
// config they were readable by anyone who could fetch the JS without logging
// in. Loaded once per session and reused.
const invoiceIssuer = ref(null);

// Fusion Eat settings an admin changes here (meals_settings): for now the
// mailbox that gets a blind copy of every invoice e-mail.
const mealsSettings = ref(null);

async function fetchMealsSettings() {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/settings");
    mealsSettings.value = data;
    return data;
  } catch (err) {
    showError(err, "Failed to load the settings");
    return null;
  }
}

/** Save settings, e.g. { invoiceBcc } → true when saved. */
async function saveMealsSettings(patch) {
  try {
    const { data } = await mealsApi.put("/api/meals/admin/settings", patch);
    mealsSettings.value = data;
    showSnackbar("Saved");
    return true;
  } catch (err) {
    showError(err, "Failed to save");
    return false;
  }
}

async function fetchInvoiceIssuer() {
  if (invoiceIssuer.value) return invoiceIssuer.value;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/invoice-issuer");
    invoiceIssuer.value = data;
    return data;
  } catch (err) {
    showError(err, "Failed to load our invoice details");
    return null;
  }
}

// ── Rate history ───────────────────────────────────────────────────────────
// A price change carries the date it starts, so a past period keeps the rate
// it was priced at. client_companies holds only what a NEW period costs.
const clientRates = ref([]);

async function fetchClientRates(clientId) {
  clientRates.value = [];
  if (!clientId) return;
  try {
    const { data } = await mealsApi.get(`/api/meals/admin/clients/${clientId}/rates`);
    clientRates.value = data;
  } catch (err) {
    showError(err, "Failed to load the rate history");
  }
}

async function addClientRate(clientId, rate) {
  try {
    await mealsApi.post(`/api/meals/admin/clients/${clientId}/rates`, rate);
    showSnackbar(`Rate from ${rate.effective_from} saved`, "success");
    await Promise.all([fetchClientRates(clientId), fetchClients()]);
    return true;
  } catch (err) {
    showError(err, "Could not save the rate");
    return false;
  }
}

async function deleteClientRate(clientId, rateId) {
  try {
    await mealsApi.delete(`/api/meals/admin/clients/${clientId}/rates/${rateId}`);
    await Promise.all([fetchClientRates(clientId), fetchClients()]);
  } catch (err) {
    showError(err, "Could not remove the rate");
  }
}

// ── Saved invoices ─────────────────────────────────────────────────────────
// A period is either invoiced or not. `invoice` holds the issued document for
// the period on screen (null while it is still a live computation), so Billing
// can show what was issued rather than recomputing at today's rate.
const invoice = ref(null);
const invoices = ref([]);
const invoiceLoading = ref(false);

async function fetchInvoiceForPeriod(clientId, start, end) {
  invoice.value = null;
  if (!clientId || !start || !end) return;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/invoices/for-period", {
      params: { clientId, start, end },
    });
    invoice.value = data || null;
  } catch (err) {
    showError(err, "Failed to check whether this period is invoiced");
  }
}

async function fetchInvoices(clientId) {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/invoices", { params: { clientId: clientId || undefined } });
    invoices.value = data;
  } catch (err) {
    showError(err, "Failed to load invoices");
  }
}

/** One invoice with its frozen lines — what the document said. */
async function fetchInvoice(id) {
  try {
    const { data } = await mealsApi.get(`/api/meals/admin/invoices/${id}`);
    return data;
  } catch (err) {
    showError(err, "Failed to load the invoice");
    return null;
  }
}

/** Freeze the period into a numbered document. Returns it, or null on refusal. */
async function issueInvoice(clientId, start, end) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post("/api/meals/admin/invoices", { clientId, start, end });
    invoice.value = data;
    // Issued even when the GL posting failed — then say so; the row offers a retry.
    if (data.gl_error) showSnackbar(`Invoice ${data.invoice_number} issued, but NOT posted to the GL: ${data.gl_error}`, "warning");
    else showSnackbar(`Invoice ${data.invoice_number} issued — posted to the GL as ${data.gl_reference}`, "success");
    // The server kept the PDF copy (in Assets, on the GL entry); say if it could not
    if (data.copy_warning) showSnackbar(`Invoice ${data.invoice_number} issued; its copy: ${data.copy_warning}`, "warning");
    // The client's credit on account (GL 2040) is taken off at issue (2026-11-02)
    if (data.credit_warning) showSnackbar(`Invoice ${data.invoice_number} issued; ${data.credit_warning}`, "warning");
    await Promise.all([fetchInvoices(clientId), fetchOwed()]);
    return data;
  } catch (err) {
    showError(err, "Could not issue the invoice");
    return null;
  } finally {
    invoiceLoading.value = false;
  }
}

/**
 * Keep the PDF copy of an issued invoice in Assets (and on its journal entry).
 * The server builds and keeps it (2026-10-05) — issuing keeps it already; this
 * is the "Keep a copy" action for an invoice issued without one. Never throws.
 */
async function keepInvoiceCopy(inv, { creditNote = false } = {}) {
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${inv.id}/keep-copy`, null, {
      params: creditNote ? { creditNote: 1 } : undefined,
    });
    if (data.warning) showSnackbar(data.warning, "warning");
    await fetchInvoices(data.client_company_id);
    return data;
  } catch (err) {
    showError(err, `The copy of ${creditNote ? inv?.credit_note_number : inv?.invoice_number} was not kept`);
    return null;
  }
}

/**
 * Open the invoice (or its credit note) as the server builds it — the same PDF
 * the client is e-mailed. The tab is opened inside the click, before the
 * download, so it is not blocked as a pop-up.
 */
async function openInvoicePdf(inv, { creditNote = false } = {}) {
  const tab = window.open("", "_blank");
  try {
    const res = await mealsApi.get(`/api/meals/admin/invoices/${inv.id}/pdf`, {
      params: creditNote ? { creditNote: 1 } : undefined,
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    if (tab) tab.location = url;
    else window.open(url, "_blank");
  } catch (err) {
    tab?.close();
    showError(err, "Could not open the invoice PDF");
  }
}

/**
 * E-mail an issued invoice now — to the client's billing contact, the office
 * manager in copy (the Monday run does this by itself for clients with the
 * automatic weekly invoice on). → the updated invoice, or null.
 */
async function sendInvoiceByEmail(inv) {
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${inv.id}/send`);
    showSnackbar(`${data.invoice_number} sent to ${data.sent_to}${data.sent_cc ? ` (cc ${data.sent_cc})` : ""}`, "success");
    await fetchInvoices(data.client_company_id);
    return data;
  } catch (err) {
    showError(err, `${inv.invoice_number} was not sent`);
    return null;
  }
}

/**
 * Withdraw an invoice already sent to the client with a tax credit note
 * (2026-09-30): the server numbers it (CN-YYYY-NNNN) and books the reverse GL
 * entry; the credit note's PDF is then built and kept like invoice copies.
 * → the updated invoice, or null.
 */
async function issueCreditNote(inv, { reason, issuedOn }) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${inv.id}/credit-note`, { reason, issuedOn });
    showSnackbar(`Credit note ${data.credit_note_number} issued for ${data.invoice_number} — GL ${data.credit_note_gl_reference || "—"}`, "success");
    if (data.copy_warning) showSnackbar(`Credit note ${data.credit_note_number}; its copy: ${data.copy_warning}`, "warning");
    await Promise.all([fetchInvoices(inv.client_company_id), fetchOwed()]);
    return data;
  } catch (err) {
    showError(err, "Could not issue the credit note");
    return null;
  } finally {
    invoiceLoading.value = false;
  }
}

/** Keep the PDF copy of an invoice's credit note (server-built). */
function keepCreditNoteCopy(inv) {
  return keepInvoiceCopy(inv, { creditNote: true });
}

/** Open any kept copy (an asset id) in a new tab — invoice or credit note. */
async function openKeptCopy(assetId) {
  try {
    const { data } = await assetsApi.get(`/api/assets/${assetId}/download`, { params: { inline: true } });
    const url = data?.url || data?.downloadUrl || data;
    if (url) window.open(url, "_blank");
    else showSnackbar("No download URL returned", "error");
  } catch (err) {
    showError(err, "Could not open the kept copy");
  }
}

/** Open the kept copy of an invoice (signed URL from Assets) in a new tab. */
async function openInvoiceCopy(inv) {
  try {
    const { data } = await assetsApi.get(`/api/assets/${inv.copy_asset_id}/download`, { params: { inline: true } });
    const url = data?.url || data?.downloadUrl || data;
    if (url) window.open(url, "_blank");
    else showSnackbar("No download URL returned", "error");
  } catch (err) {
    showError(err, "Could not open the kept copy");
  }
}

/**
 * Cancel an issued invoice so the period can be corrected and issued again.
 * The server reverses its GL entry first. Returns true when cancelled.
 */
async function cancelInvoice(id, clientId) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${id}/cancel`);
    invoice.value = null;
    showSnackbar(
      `Invoice ${data.invoice_number} cancelled`
        + (data.gl_reversal_reference ? ` — GL entry reversed by ${data.gl_reversal_reference}` : ""),
      "success",
    );
    await Promise.all([fetchInvoices(clientId), fetchOwed()]);
    return true;
  } catch (err) {
    showError(err, "Could not cancel the invoice");
    return false;
  } finally {
    invoiceLoading.value = false;
  }
}

// ── Payments and what clients owe ─────────────────────────────────────────────
const owed = ref(null);

/** Issued, unpaid invoices of every client + the GL 1045 check. */
async function fetchOwed() {
  try {
    const { data } = await mealsApi.get("/api/meals/admin/invoices/owed");
    owed.value = data;
  } catch (err) {
    showError(err, "Failed to load what clients owe");
  }
}

/** The client paid the invoice: posts Dr 1011 (or 1010) / Cr 1045 and marks it paid. */
async function payInvoice(id, clientId, { paidOn, method, reference }) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${id}/pay`, { paidOn, method, reference });
    if (invoice.value?.id === data.id) invoice.value = { ...invoice.value, ...data };
    showSnackbar(`Invoice ${data.invoice_number} paid in cash — GL entry ${data.payment_gl_reference}`, "success");
    await Promise.all([fetchInvoices(clientId), fetchOwed()]);
    return true;
  } catch (err) {
    showError(err, "Could not mark the invoice paid");
    return false;
  } finally {
    invoiceLoading.value = false;
  }
}

/** Undo a payment recorded by mistake (its GL entry is reversed). */
async function unpayInvoice(id, clientId) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${id}/unpay`);
    if (invoice.value?.id === data.id) invoice.value = { ...invoice.value, ...data };
    showSnackbar(`Invoice ${data.invoice_number} is unpaid again — the payment entry was reversed`, "success");
    await Promise.all([fetchInvoices(clientId), fetchOwed()]);
  } catch (err) {
    showError(err, "Could not undo the payment");
  } finally {
    invoiceLoading.value = false;
  }
}

/** Post an issued invoice the GL does not have yet (a retry, or one issued before posting existed). */
async function postInvoiceGl(id, clientId) {
  invoiceLoading.value = true;
  try {
    const { data } = await mealsApi.post(`/api/meals/admin/invoices/${id}/post-gl`);
    if (invoice.value?.id === data.id) invoice.value = { ...invoice.value, ...data };
    showSnackbar(`Invoice ${data.invoice_number} posted to the GL as ${data.gl_reference}`, "success");
    await Promise.all([fetchInvoices(clientId), fetchOwed()]);
  } catch (err) {
    showError(err, "Could not post the invoice to the GL");
  } finally {
    invoiceLoading.value = false;
  }
}

async function fetchBilling(clientId, start, end) {
  if (!clientId || !start || !end) return;
  billingLoading.value = true;
  try {
    const { data } = await mealsApi.get("/api/meals/admin/billing", { params: { clientId, start, end } });
    billing.value = data;
  } catch (err) {
    showError(err, "Failed to load billing report");
  } finally {
    billingLoading.value = false;
  }
}

export function useMeals() {
  return {
    snackbar, showSnackbar,
    // clients
    clients, clientsLoading, clientDialog, editingClient, clientForm, clientSaving,
    fetchClients, openClientDialog, closeClientDialog, saveClient, setClientActive,
    selectedClientId, selectedClient,
    // employees
    employees, employeesLoading, employeeDialog, editingEmployee, employeeForm, employeeSaving,
    filters, datePreset, datePresets, applyDatePreset, report,
    fetchEmployees, openEmployeeDialog, closeEmployeeDialog, saveEmployee, setEmployeeActive, regenerateLink,
    openLateWindow, closeLateWindow,
    // employee leave
    employeeLeave, employeeLeaveLoading, leaveDialog, leaveEmployee, leaveForm, leaveSaving,
    fetchEmployeeLeave, openLeaveDialog, closeLeaveDialog, addLeave, removeLeave,
    leaveInRange, fetchLeaveInRange,
    // meal sets
    mealSets, mealSetsLoading, mealSetDialog, editingMealSet, mealSetForm, mealSetSaving,
    fetchMealSets, openMealSetDialog, closeMealSetDialog, saveMealSet, setMealSetActive,
    mealSetPhoto, mealSetImageUrl, chooseMealSetPhoto, removeMealSetPhoto,
    recipes, fetchRecipes,
    // drinks
    drinks, drinksLoading, drinkDialog, editingDrink, drinkForm, drinkSaving,
    fetchDrinks, openDrinkDialog, closeDrinkDialog, saveDrink, setDrinkActive,
    // weekly menu
    weeklyMenuDays, weeklyMenuLoading, fetchWeeklyMenu, saveMenuDay,
    // orders board
    orders, ordersLoading, fetchOrders, saveOrder, deleteOrder,
    packList, packListLoading, fetchPackList,
    menuPart, riceDemand, fetchRiceDemand,
    weeklyPackList, weeklyPackListLoading, fetchWeeklyPackList,
    // production
    production, productionLoading, fetchProduction,
    weeklySchedule, weeklyScheduleLoading, fetchWeeklySchedule,
    // procurement
    procurement, procurementLoading, fetchProcurement,
    // billing
    billing, billingLoading, fetchBilling,
    invoiceIssuer, fetchInvoiceIssuer,
    mealsSettings, fetchMealsSettings, saveMealsSettings,
    // rate history
    clientRates, fetchClientRates, addClientRate, deleteClientRate,
    // saved invoices
    invoice, invoices, invoiceLoading,
    fetchInvoiceForPeriod, fetchInvoices, fetchInvoice, issueInvoice, cancelInvoice, postInvoiceGl,
    keepInvoiceCopy, issueCreditNote, keepCreditNoteCopy, openKeptCopy, openInvoiceCopy, openInvoicePdf, sendInvoiceByEmail,
    owed, fetchOwed, payInvoice, unpayInvoice,
  };
}
